'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef, type PropsWithChildren } from 'react';
import { Controller, type FieldError, type FieldValues, type RegisterOptions } from 'react-hook-form';
import ReactSelect, { components, type GroupBase, type MultiValueRemoveProps, type Props, type SelectInstance } from 'react-select';
import { type SelectOption } from '@shared/utils/types/select-option';
import { FieldErrorMessage } from '../field-error-message/field-error-message';
import CloseIcon from '../svgs/close-icon';
import SvgErrorIcon from '../svgs/error-icon';
import styles from './field-controller.module.scss';
// eslint-disable-next-line import/no-unassigned-import
import './select.scss';
export type SelectFieldProps<Option = SelectOption, IsMulti extends boolean = boolean, Group extends GroupBase<Option> = GroupBase<Option>> = BaseFieldProps & Props<Option, IsMulti, Group> & {
  error?: FieldError;
  hint?: string;
  isRequired?: boolean;
  maximumSelectedOptions?: number;
  noOptionsLabel?: string;
  withoutController?: boolean;
};
type BaseFieldProps = {
  dataTestId?: string;
  hasOptionalLabel?: boolean;
  id?: string;
  label: string;
  name: string;
  optionalLabel?: string;

  // eslint-disable-next-line react/no-unused-prop-types
  rules?: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'setValueAs' | 'valueAsDate' | 'valueAsNumber'>;
};
const FieldWrapper = t0 => {
  const $ = _c(20);
  const {
    children,
    dataTestId,
    error,
    hasOptionalLabel: t1,
    hint,
    id,
    label,
    name,
    optionalLabel: t2
  } = t0;
  const hasOptionalLabel = t1 === undefined ? true : t1;
  const optionalLabel = t2 === undefined ? "optional" : t2;
  const fieldId = name ?? id;
  const errorMessageId = `${fieldId}-error`;
  const t3 = dataTestId ?? fieldId;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classNames(styles.field__label, styles["field__label--select"]);
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const t5 = hasOptionalLabel && `(${optionalLabel})`;
  let t6;
  if ($[1] !== fieldId || $[2] !== label || $[3] !== t5) {
    t6 = <label className={t4} htmlFor={fieldId}>{label} {t5}</label>;
    $[1] = fieldId;
    $[2] = label;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== error || $[6] !== hint) {
    t7 = !error && hint && <p className={styles.field__hint}>{hint}</p>;
    $[5] = error;
    $[6] = hint;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== error?.message) {
    t8 = error?.message && <SvgErrorIcon className={styles["field__error-icon"]} />;
    $[8] = error?.message;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== error || $[11] !== errorMessageId) {
    t9 = error?.message && <FieldErrorMessage id={errorMessageId} message={error.message} />;
    $[10] = error;
    $[11] = errorMessageId;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== children || $[14] !== t3 || $[15] !== t6 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t10 = <div className={styles.field} data-testid={t3}>{children}{t6}{t7}{t8}{t9}</div>;
    $[13] = children;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
const MultiValueRemove = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CloseIcon className="react-select__remove-selected-option-icon" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <components.MultiValueRemove {...props}>{t0}</components.MultiValueRemove>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const SelectFieldComponent = forwardRef<SelectInstance<SelectOption, boolean, GroupBase<SelectOption>>, SelectFieldProps>((t0, ref) => {
  const $ = _c(54);
  let error;
  let hint;
  let id;
  let isDisabled;
  let isMulti;
  let label;
  let maximumSelectedOptions;
  let name;
  let noOptionsLabel;
  let onChange;
  let optionalLabel;
  let options;
  let restSelectFieldProps;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      error,
      hint,
      id,
      isDisabled,
      isMulti,
      isRequired: t1,
      label,
      maximumSelectedOptions,
      name,
      noOptionsLabel,
      onChange,
      optionalLabel,
      options,
      value,
      ...restSelectFieldProps
    } = t0);
    $[0] = t0;
    $[1] = error;
    $[2] = hint;
    $[3] = id;
    $[4] = isDisabled;
    $[5] = isMulti;
    $[6] = label;
    $[7] = maximumSelectedOptions;
    $[8] = name;
    $[9] = noOptionsLabel;
    $[10] = onChange;
    $[11] = optionalLabel;
    $[12] = options;
    $[13] = restSelectFieldProps;
    $[14] = t1;
    $[15] = value;
  } else {
    error = $[1];
    hint = $[2];
    id = $[3];
    isDisabled = $[4];
    isMulti = $[5];
    label = $[6];
    maximumSelectedOptions = $[7];
    name = $[8];
    noOptionsLabel = $[9];
    onChange = $[10];
    optionalLabel = $[11];
    options = $[12];
    restSelectFieldProps = $[13];
    t1 = $[14];
    value = $[15];
  }
  const isRequired = t1 === undefined ? false : t1;
  const inputId = name ?? id;
  const errorMessageId = `${inputId}-error`;
  const hasError = Boolean(error);
  const hasValue = (Array.isArray(value) ? value.length : value) || value && !isMulti;
  const optionsLength = Array.isArray(value) ? value.length : 0;
  const t2 = !isRequired && !isDisabled && restSelectFieldProps.hasOptionalLabel;
  const t3 = (Boolean(restSelectFieldProps.isClearable) || isMulti) && hasValue;
  let t4;
  if ($[16] !== hasError || $[17] !== hasValue || $[18] !== t3) {
    t4 = classNames("react-select-container", {
      "react-select-container--has-error": hasError,
      "react-select-container--has-value": hasValue,
      "react-select-container--is-clearable": t3
    });
    $[16] = hasError;
    $[17] = hasValue;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== hasError || $[21] !== hasValue) {
    t5 = {
      control: () => classNames("react-select", {
        "react-select--has-error": hasError,
        "react-select--has-value": hasValue
      })
    };
    $[20] = hasError;
    $[21] = hasValue;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      MultiValueRemove,
      Placeholder: _temp
    };
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== isMulti || $[25] !== maximumSelectedOptions || $[26] !== optionsLength) {
    t7 = maximumSelectedOptions && isMulti ? () => optionsLength >= maximumSelectedOptions : _temp2;
    $[24] = isMulti;
    $[25] = maximumSelectedOptions;
    $[26] = optionsLength;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== noOptionsLabel) {
    t8 = () => noOptionsLabel;
    $[28] = noOptionsLabel;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== errorMessageId || $[31] !== hasError || $[32] !== inputId || $[33] !== isDisabled || $[34] !== isMulti || $[35] !== isRequired || $[36] !== onChange || $[37] !== options || $[38] !== ref || $[39] !== restSelectFieldProps || $[40] !== t4 || $[41] !== t5 || $[42] !== t7 || $[43] !== t8 || $[44] !== value) {
    t9 = <ReactSelect {...restSelectFieldProps} aria-describedby={errorMessageId} aria-invalid={hasError} aria-required={isRequired} className={t4} classNamePrefix="react-select" classNames={t5} components={t6} inputId={inputId} isDisabled={isDisabled} isMulti={isMulti} isOptionDisabled={t7} noOptionsMessage={t8} onChange={onChange} options={options} ref={ref} value={value} />;
    $[30] = errorMessageId;
    $[31] = hasError;
    $[32] = inputId;
    $[33] = isDisabled;
    $[34] = isMulti;
    $[35] = isRequired;
    $[36] = onChange;
    $[37] = options;
    $[38] = ref;
    $[39] = restSelectFieldProps;
    $[40] = t4;
    $[41] = t5;
    $[42] = t7;
    $[43] = t8;
    $[44] = value;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== error || $[47] !== hint || $[48] !== label || $[49] !== name || $[50] !== optionalLabel || $[51] !== t2 || $[52] !== t9) {
    t10 = <FieldWrapper error={error} hasOptionalLabel={t2} hint={hint} label={label} name={name} optionalLabel={optionalLabel}>{t9}</FieldWrapper>;
    $[46] = error;
    $[47] = hint;
    $[48] = label;
    $[49] = name;
    $[50] = optionalLabel;
    $[51] = t2;
    $[52] = t9;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  return t10;
});
export const SelectField = t0 => {
  const $ = _c(16);
  let controlledValue;
  let name;
  let onChange;
  let restSelectFieldProps;
  let rules;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      isRequired: t1,
      name,
      onChange,
      rules,
      value: controlledValue,
      withoutController: t2,
      ...restSelectFieldProps
    } = t0);
    $[0] = t0;
    $[1] = controlledValue;
    $[2] = name;
    $[3] = onChange;
    $[4] = restSelectFieldProps;
    $[5] = rules;
    $[6] = t1;
    $[7] = t2;
  } else {
    controlledValue = $[1];
    name = $[2];
    onChange = $[3];
    restSelectFieldProps = $[4];
    rules = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const isRequired = t1 === undefined ? false : t1;
  const withoutController = t2 === undefined ? false : t2;
  let t3;
  if ($[8] !== controlledValue || $[9] !== isRequired || $[10] !== name || $[11] !== onChange || $[12] !== restSelectFieldProps || $[13] !== rules || $[14] !== withoutController) {
    t3 = withoutController ? <SelectFieldComponent isRequired={isRequired} name={name} onChange={onChange} value={controlledValue} {...restSelectFieldProps} /> : <Controller name={name} render={t4 => {
      const {
        field: t5,
        fieldState: t6
      } = t4;
      const {
        onChange: fieldOnChange,
        value,
        ...restFieldProps
      } = t5;
      const {
        error
      } = t6;
      const isRequiredOrHasRequiredRule = isRequired || Boolean(rules?.required);
      return <SelectFieldComponent error={error} isRequired={isRequiredOrHasRequiredRule} onChange={(newValue, actionMeta) => {
        if (onChange) {
          onChange(newValue, actionMeta);
        }
        fieldOnChange(newValue);
      }} value={value} {...restSelectFieldProps} {...restFieldProps} />;
    }} rules={rules} />;
    $[8] = controlledValue;
    $[9] = isRequired;
    $[10] = name;
    $[11] = onChange;
    $[12] = restSelectFieldProps;
    $[13] = rules;
    $[14] = withoutController;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
function _temp() {
  return null;
}
function _temp2() {
  return false;
}